import { tz } from "@date-fns/tz";
import { Grid2, Typography, useTheme } from "@mui/material";
import { format, intervalToDuration } from "date-fns";
import { useMemo } from "react";
import SunCalc from "suncalc";

import { useFiltersContext } from "components/contexts";
import { Widget } from "components/elements/mui";
import { MessageKey, useFormatMessage } from "lang";
import { getSelectedPowerPlant, useAppSelector } from "store";

export const DaylightInfo = () => {
  const theme = useTheme();
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = tz(selectedPowerPlant?.timezone || "Europe/Warsaw");
  const { date } = useFiltersContext();

  const sunInfo = useMemo(
    () =>
      selectedPowerPlant
        ? SunCalc.getTimes(
            // Noon, not midnight: SunCalc snaps to the nearest solar noon, and
            // local midnight in summer is 22:00 UTC of the day before — so the
            // card showed the previous day's sunrise and sunset all summer.
            new Date(date.day.getTime() + 12 * 60 * 60 * 1000),
            +selectedPowerPlant.lat,
            +selectedPowerPlant.lng,
          )
        : undefined,
    [date.day, selectedPowerPlant],
  );

  if (!sunInfo) return null;

  return (
    <Widget
      size={{
        xs: 12,
      }}
      styles={{ cardContent: { padding: 0, paddingBottom: "0 !important" } }}
      content={
        <Grid2 container size={12} spacing={1} justifyContent={"space-between"}>
          <Grid2
            container
            direction={"column"}
            spacing={0}
            justifyContent="flex-end"
            width={"100%"}
          >
            {(
              [
                {
                  name: "weather.firstLight",
                  value: format(new Date(sunInfo.dawn), "HH:mm", { in: timezone }),
                },
                {
                  name: "weather.sunrise",
                  value: format(new Date(sunInfo.sunrise), "HH:mm", { in: timezone }),
                },
                {
                  name: "weather.sunset",
                  value: format(new Date(sunInfo.sunset), "HH:mm", { in: timezone }),
                },
                {
                  name: "weather.lastLight",
                  value: format(new Date(sunInfo.dusk), "HH:mm", { in: timezone }),
                },
                {
                  name: "weather.totalDaylight",
                  value: (() => {
                    const duration = intervalToDuration({
                      start: new Date(sunInfo.sunrise),
                      end: new Date(sunInfo.sunset),
                    });
                    return `${duration.hours}h ${duration.minutes}min`;
                  })(),
                },
              ] satisfies { name: MessageKey; value: string }[]
            ).map(({ name, value }) => (
              <Grid2
                key={name}
                container
                spacing={1}
                sx={{
                  padding: "8px",
                  "&:last-child": { borderBottom: "none" },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  align="right"
                  fontSize={16}
                  fontWeight={500}
                >
                  {formatMessage(name)}
                </Typography>
                <Typography
                  variant="h6"
                  align="right"
                  fontSize={16}
                  fontWeight={400}
                  color="text.secondary"
                >
                  {value}
                </Typography>
              </Grid2>
            ))}
          </Grid2>
        </Grid2>
      }
    />
  );
};
